/**
 * Componente de ruta protegida
 * 
 * Verifica que el usuario esté autenticado antes de permitir el acceso.
 * Si no está autenticado, redirige al login.
 * Muestra un loader mientras se verifica el estado de autenticación.
 * 
 * Uso:
 * <ProtectedRoute>
 *   <ComponenteProtegido />
 * </ProtectedRoute>
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Mostrar loader mientras se verifica la autenticación
  // Esto previene el flickering de redirección
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  // Si no hay usuario autenticado, redirigir al login
  // replace=true evita que el usuario pueda volver atrás
  // Pasar la ruta actual como redirect para volver después del login
  if (!user) {
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Usuario autenticado, renderizar el contenido protegido
  return <>{children}</>;
}
